import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { Home, Calendar, BookOpen, ClipboardList, LogOut, Globe } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import axios from 'axios';

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 'https://suiv.carfeis.com/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('prof_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Translations
const translations = {
  fr: {
    welcome: 'Bienvenue',
    teacherSpace: 'Espace Professeurs',
    email: 'Email',
    password: 'Mot de passe',
    login: 'Se connecter',
    logging: 'Connexion...',
    loginSuccess: 'Connexion réussie',
    loginError: 'Email ou mot de passe incorrect',
    myClasses: 'Mes Classes',
    noClassAssigned: 'Aucune classe assignée',
    capacity: 'Capacité',
    students: 'élèves',
    home: 'Accueil',
    week: 'Semaine',
    month: 'Mois',
    trimester: '3 Mois',
    logout: 'Sortir',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    saved: 'Enregistré !',
    noEval: 'Aucune évaluation',
    error: 'Erreur',
    loadError: 'Erreur de chargement',
    selectClass: 'Sélectionner une classe',
    selectMonth: 'Sélectionner le mois',
    level: 'Niveau',
    grade: 'Note',
    appreciation: 'Appréciation',
    excellent: 'Excellent',
    good: 'Bien',
    average: 'Moyen',
    poor: 'Insuffisant',
    reasons: 'Raisons',
    chooseLanguage: 'Choisir la langue',
    french: 'Français',
    arabic: 'العربية',
    months: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
    days: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'],
  },
  ar: {
    welcome: 'مرحبا',
    teacherSpace: 'فضاء المعلمين',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    login: 'تسجيل الدخول',
    logging: 'جاري التسجيل...',
    loginSuccess: 'تم تسجيل الدخول',
    loginError: 'البريد أو كلمة المرور غير صحيحة',
    myClasses: 'فصولي',
    noClassAssigned: 'لا توجد فصول',
    capacity: 'السعة',
    students: 'طلاب',
    home: 'الرئيسية',
    week: 'الأسبوع',
    month: 'الشهر',
    trimester: '٣ أشهر',
    logout: 'خروج',
    save: 'حفظ',
    saving: 'جاري الحفظ...',
    saved: 'تم الحفظ!',
    noEval: 'لا توجد تقييمات',
    error: 'خطأ',
    loadError: 'خطأ في التحميل',
    selectClass: 'اختر الفصل',
    selectMonth: 'اختر الشهر',
    level: 'المستوى',
    grade: 'الدرجة',
    appreciation: 'التقدير',
    excellent: 'ممتاز',
    good: 'جيد',
    average: 'متوسط',
    poor: 'ضعيف',
    reasons: 'الأسباب',
    chooseLanguage: 'اختر اللغة',
    french: 'Français',
    arabic: 'العربية',
    months: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
    days: ['إثن', 'ثلا', 'أرب', 'خمي', 'جمع'],
  }
};

// Language Context
const LangContext = createContext(null);
export const useLang = () => useContext(LangContext);

function LangProvider({ children }) {
  const [lang, setLang] = useState(localStorage.getItem('prof_lang') || '');
  const [showLangPicker, setShowLangPicker] = useState(!localStorage.getItem('prof_lang'));

  const t = (key) => translations[lang]?.[key] || translations.fr[key] || key;

  const selectLanguage = (selectedLang) => {
    setLang(selectedLang);
    localStorage.setItem('prof_lang', selectedLang);
    setShowLangPicker(false);
  };

  const isRTL = lang === 'ar';

  return (
    <LangContext.Provider value={{ lang, t, selectLanguage, isRTL, showLangPicker }}>
      {children}
    </LangContext.Provider>
  );
}

// Auth Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('prof_token');
    const userData = localStorage.getItem('prof_user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('prof_token', access_token);
    localStorage.setItem('prof_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('prof_token');
    localStorage.removeItem('prof_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// Language Picker Screen
function LanguagePicker() {
  const { selectLanguage } = useLang();

  return (
    <div className="min-h-screen flex flex-col justify-center items-center p-6 bg-gradient-to-b from-green-800 to-green-900">
      <div className="text-center mb-10">
        <h1 className="text-5xl font-bold text-white mb-4">دار الإيمان</h1>
        <p className="text-green-200 text-lg">Daar Al-Iman</p>
      </div>
      
      <div className="w-full max-w-xs space-y-4">
        <button
          onClick={() => selectLanguage('ar')}
          className="w-full bg-white text-green-800 py-5 rounded-2xl text-2xl font-bold shadow-lg active:scale-95 transition-transform"
        >
          العربية
        </button>
        
        <button
          onClick={() => selectLanguage('fr')}
          className="w-full bg-white/20 text-white py-5 rounded-2xl text-xl font-bold border-2 border-white/30 active:scale-95 transition-transform"
        >
          Français
        </button>
      </div>
      
      <div className="mt-10 flex items-center gap-2 text-green-200">
        <Globe className="w-5 h-5" />
        <span>اختر اللغة / Choisir la langue</span>
      </div>
    </div>
  );
}

// Login Page
function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, isRTL } = useLang();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
      toast.success(t('loginSuccess'));
    } catch (error) {
      toast.error(t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center p-6 bg-gradient-to-b from-green-800 to-green-900" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">دار الإيمان</h1>
        <p className="text-green-200">{t('teacherSpace')}</p>
      </div>
      
      <div className="card p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? t('logging') : t('login')}
          </button>
        </form>
      </div>
    </div>
  );
}

// Dashboard Page
function DashboardPage() {
  const { user } = useAuth();
  const { t, isRTL } = useLang();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const res = await api.get('/classes');
      
      // Admin et Direction voient toutes les classes
      // Les enseignants voient uniquement leurs classes
      const userRole = user?.role;
      let myClasses;
      
      if (userRole === 'admin' || userRole === 'direction') {
        myClasses = res.data;
      } else {
        const teacherId = user?.teacher_id;
        myClasses = teacherId 
          ? res.data.filter(c => c.teacher_id === teacherId)
          : [];
      }
      
      setClasses(myClasses);
    } catch (error) {
      toast.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="card p-4">
        <h2 className="text-lg font-bold mb-1">{t('welcome')} 👋</h2>
        <p className="text-gray-600">{user?.first_name} {user?.last_name}</p>
      </div>

      <h3 className="font-bold text-gray-700 px-1">{t('myClasses')}</h3>
      
      {classes.length === 0 ? (
        <div className="card p-6 text-center text-gray-500">
          {t('noClassAssigned')}
        </div>
      ) : (
        <div className="space-y-3">
          {classes.map(cls => (
            <div key={cls.id} className="card p-4">
              <h4 className="font-bold text-green-800">{cls.name}</h4>
              <p className="text-sm text-gray-500">{t('capacity')}: {cls.capacity} {t('students')}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Weekly Evaluation Page - Vue jour par jour pour mobile
function WeeklyEvalPage() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDayIndex, setSelectedDayIndex] = useState(new Date().getDay() === 0 ? 4 : Math.min(new Date().getDay() - 1, 4));
  const [weekOffset, setWeekOffset] = useState(0);
  const [evalType, setEvalType] = useState('bind');
  const [evaluations, setEvaluations] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [studentSurahData, setStudentSurahData] = useState({});
  const [studentProgress, setStudentProgress] = useState({}); // Progression des élèves
  const { user } = useAuth();
  const { t, isRTL } = useLang();

  const DAYS = [
    { name: 'Lundi', name_ar: 'الإثنين', short: 'Lun' },
    { name: 'Mardi', name_ar: 'الثلاثاء', short: 'Mar' },
    { name: 'Mercredi', name_ar: 'الأربعاء', short: 'Mer' },
    { name: 'Jeudi', name_ar: 'الخميس', short: 'Jeu' },
    { name: 'Vendredi', name_ar: 'الجمعة', short: 'Ven' },
  ];

  // Liste complète des 114 sourates
  const SURAHS = [
    { id: 1, name: 'الفاتحة', name_fr: 'Al-Fatiha', verses: 7 },
    { id: 2, name: 'البقرة', name_fr: 'Al-Baqara', verses: 286 },
    { id: 3, name: 'آل عمران', name_fr: 'Al-Imran', verses: 200 },
    { id: 4, name: 'النساء', name_fr: 'An-Nisa', verses: 176 },
    { id: 5, name: 'المائدة', name_fr: 'Al-Maida', verses: 120 },
    { id: 6, name: 'الأنعام', name_fr: 'Al-Anam', verses: 165 },
    { id: 7, name: 'الأعراف', name_fr: 'Al-Araf', verses: 206 },
    { id: 8, name: 'الأنفال', name_fr: 'Al-Anfal', verses: 75 },
    { id: 9, name: 'التوبة', name_fr: 'At-Tawba', verses: 129 },
    { id: 10, name: 'يونس', name_fr: 'Yunus', verses: 109 },
    { id: 11, name: 'هود', name_fr: 'Hud', verses: 123 },
    { id: 12, name: 'يوسف', name_fr: 'Yusuf', verses: 111 },
    { id: 13, name: 'الرعد', name_fr: 'Ar-Rad', verses: 43 },
    { id: 14, name: 'إبراهيم', name_fr: 'Ibrahim', verses: 52 },
    { id: 15, name: 'الحجر', name_fr: 'Al-Hijr', verses: 99 },
    { id: 16, name: 'النحل', name_fr: 'An-Nahl', verses: 128 },
    { id: 17, name: 'الإسراء', name_fr: 'Al-Isra', verses: 111 },
    { id: 18, name: 'الكهف', name_fr: 'Al-Kahf', verses: 110 },
    { id: 19, name: 'مريم', name_fr: 'Maryam', verses: 98 },
    { id: 20, name: 'طه', name_fr: 'Ta-Ha', verses: 135 },
    { id: 21, name: 'الأنبياء', name_fr: 'Al-Anbiya', verses: 112 },
    { id: 22, name: 'الحج', name_fr: 'Al-Hajj', verses: 78 },
    { id: 23, name: 'المؤمنون', name_fr: 'Al-Muminun', verses: 118 },
    { id: 24, name: 'النور', name_fr: 'An-Nur', verses: 64 },
    { id: 25, name: 'الفرقان', name_fr: 'Al-Furqan', verses: 77 },
    { id: 26, name: 'الشعراء', name_fr: 'Ash-Shuara', verses: 227 },
    { id: 27, name: 'النمل', name_fr: 'An-Naml', verses: 93 },
    { id: 28, name: 'القصص', name_fr: 'Al-Qasas', verses: 88 },
    { id: 29, name: 'العنكبوت', name_fr: 'Al-Ankabut', verses: 69 },
    { id: 30, name: 'الروم', name_fr: 'Ar-Rum', verses: 60 },
    { id: 31, name: 'لقمان', name_fr: 'Luqman', verses: 34 },
    { id: 32, name: 'السجدة', name_fr: 'As-Sajda', verses: 30 },
    { id: 33, name: 'الأحزاب', name_fr: 'Al-Ahzab', verses: 73 },
    { id: 34, name: 'سبأ', name_fr: 'Saba', verses: 54 },
    { id: 35, name: 'فاطر', name_fr: 'Fatir', verses: 45 },
    { id: 36, name: 'يس', name_fr: 'Ya-Sin', verses: 83 },
    { id: 37, name: 'الصافات', name_fr: 'As-Saffat', verses: 182 },
    { id: 38, name: 'ص', name_fr: 'Sad', verses: 88 },
    { id: 39, name: 'الزمر', name_fr: 'Az-Zumar', verses: 75 },
    { id: 40, name: 'غافر', name_fr: 'Ghafir', verses: 85 },
    { id: 41, name: 'فصلت', name_fr: 'Fussilat', verses: 54 },
    { id: 42, name: 'الشورى', name_fr: 'Ash-Shura', verses: 53 },
    { id: 43, name: 'الزخرف', name_fr: 'Az-Zukhruf', verses: 89 },
    { id: 44, name: 'الدخان', name_fr: 'Ad-Dukhan', verses: 59 },
    { id: 45, name: 'الجاثية', name_fr: 'Al-Jathiya', verses: 37 },
    { id: 46, name: 'الأحقاف', name_fr: 'Al-Ahqaf', verses: 35 },
    { id: 47, name: 'محمد', name_fr: 'Muhammad', verses: 38 },
    { id: 48, name: 'الفتح', name_fr: 'Al-Fath', verses: 29 },
    { id: 49, name: 'الحجرات', name_fr: 'Al-Hujurat', verses: 18 },
    { id: 50, name: 'ق', name_fr: 'Qaf', verses: 45 },
    { id: 51, name: 'الذاريات', name_fr: 'Adh-Dhariyat', verses: 60 },
    { id: 52, name: 'الطور', name_fr: 'At-Tur', verses: 49 },
    { id: 53, name: 'النجم', name_fr: 'An-Najm', verses: 62 },
    { id: 54, name: 'القمر', name_fr: 'Al-Qamar', verses: 55 },
    { id: 55, name: 'الرحمن', name_fr: 'Ar-Rahman', verses: 78 },
    { id: 56, name: 'الواقعة', name_fr: 'Al-Waqia', verses: 96 },
    { id: 57, name: 'الحديد', name_fr: 'Al-Hadid', verses: 29 },
    { id: 58, name: 'المجادلة', name_fr: 'Al-Mujadila', verses: 22 },
    { id: 59, name: 'الحشر', name_fr: 'Al-Hashr', verses: 24 },
    { id: 60, name: 'الممتحنة', name_fr: 'Al-Mumtahina', verses: 13 },
    { id: 61, name: 'الصف', name_fr: 'As-Saff', verses: 14 },
    { id: 62, name: 'الجمعة', name_fr: 'Al-Jumua', verses: 11 },
    { id: 63, name: 'المنافقون', name_fr: 'Al-Munafiqun', verses: 11 },
    { id: 64, name: 'التغابن', name_fr: 'At-Taghabun', verses: 18 },
    { id: 65, name: 'الطلاق', name_fr: 'At-Talaq', verses: 12 },
    { id: 66, name: 'التحريم', name_fr: 'At-Tahrim', verses: 12 },
    { id: 67, name: 'الملك', name_fr: 'Al-Mulk', verses: 30 },
    { id: 68, name: 'القلم', name_fr: 'Al-Qalam', verses: 52 },
    { id: 69, name: 'الحاقة', name_fr: 'Al-Haqqa', verses: 52 },
    { id: 70, name: 'المعارج', name_fr: 'Al-Maarij', verses: 44 },
    { id: 71, name: 'نوح', name_fr: 'Nuh', verses: 28 },
    { id: 72, name: 'الجن', name_fr: 'Al-Jinn', verses: 28 },
    { id: 73, name: 'المزمل', name_fr: 'Al-Muzzammil', verses: 20 },
    { id: 74, name: 'المدثر', name_fr: 'Al-Muddaththir', verses: 56 },
    { id: 75, name: 'القيامة', name_fr: 'Al-Qiyama', verses: 40 },
    { id: 76, name: 'الإنسان', name_fr: 'Al-Insan', verses: 31 },
    { id: 77, name: 'المرسلات', name_fr: 'Al-Mursalat', verses: 50 },
    { id: 78, name: 'النبأ', name_fr: 'An-Naba', verses: 40 },
    { id: 79, name: 'النازعات', name_fr: 'An-Naziat', verses: 46 },
    { id: 80, name: 'عبس', name_fr: 'Abasa', verses: 42 },
    { id: 81, name: 'التكوير', name_fr: 'At-Takwir', verses: 29 },
    { id: 82, name: 'الإنفطار', name_fr: 'Al-Infitar', verses: 19 },
    { id: 83, name: 'المطففين', name_fr: 'Al-Mutaffifin', verses: 36 },
    { id: 84, name: 'الإنشقاق', name_fr: 'Al-Inshiqaq', verses: 25 },
    { id: 85, name: 'البروج', name_fr: 'Al-Buruj', verses: 22 },
    { id: 86, name: 'الطارق', name_fr: 'At-Tariq', verses: 17 },
    { id: 87, name: 'الأعلى', name_fr: 'Al-Ala', verses: 19 },
    { id: 88, name: 'الغاشية', name_fr: 'Al-Ghashiya', verses: 26 },
    { id: 89, name: 'الفجر', name_fr: 'Al-Fajr', verses: 30 },
    { id: 90, name: 'البلد', name_fr: 'Al-Balad', verses: 20 },
    { id: 91, name: 'الشمس', name_fr: 'Ash-Shams', verses: 15 },
    { id: 92, name: 'الليل', name_fr: 'Al-Layl', verses: 21 },
    { id: 93, name: 'الضحى', name_fr: 'Ad-Duha', verses: 11 },
    { id: 94, name: 'الشرح', name_fr: 'Ash-Sharh', verses: 8 },
    { id: 95, name: 'التين', name_fr: 'At-Tin', verses: 8 },
    { id: 96, name: 'العلق', name_fr: 'Al-Alaq', verses: 19 },
    { id: 97, name: 'القدر', name_fr: 'Al-Qadr', verses: 5 },
    { id: 98, name: 'البينة', name_fr: 'Al-Bayyina', verses: 8 },
    { id: 99, name: 'الزلزلة', name_fr: 'Az-Zalzala', verses: 8 },
    { id: 100, name: 'العاديات', name_fr: 'Al-Adiyat', verses: 11 },
    { id: 101, name: 'القارعة', name_fr: 'Al-Qaria', verses: 11 },
    { id: 102, name: 'التكاثر', name_fr: 'At-Takathur', verses: 8 },
    { id: 103, name: 'العصر', name_fr: 'Al-Asr', verses: 3 },
    { id: 104, name: 'الهمزة', name_fr: 'Al-Humaza', verses: 9 },
    { id: 105, name: 'الفيل', name_fr: 'Al-Fil', verses: 5 },
    { id: 106, name: 'قريش', name_fr: 'Quraysh', verses: 4 },
    { id: 107, name: 'الماعون', name_fr: 'Al-Maun', verses: 7 },
    { id: 108, name: 'الكوثر', name_fr: 'Al-Kawthar', verses: 3 },
    { id: 109, name: 'الكافرون', name_fr: 'Al-Kafirun', verses: 6 },
    { id: 110, name: 'النصر', name_fr: 'An-Nasr', verses: 3 },
    { id: 111, name: 'المسد', name_fr: 'Al-Masad', verses: 5 },
    { id: 112, name: 'الإخلاص', name_fr: 'Al-Ikhlas', verses: 4 },
    { id: 113, name: 'الفلق', name_fr: 'Al-Falaq', verses: 5 },
    { id: 114, name: 'الناس', name_fr: 'An-Nas', verses: 6 },
  ];

  const STATUSES = [
    { value: 'suivi', label: 'حـ', name: 'Suivi', color: '#22c55e' },
    { value: 'non_suivi', label: 'لم', name: 'Non suivi', color: '#ef4444' },
    { value: 'absent', label: 'غ', name: 'Absent', color: '#6b7280' },
    { value: 'malade', label: 'م', name: 'Malade', color: '#eab308' },
  ];

  const getWeekDates = () => {
    const now = new Date();
    const day = now.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + mondayOffset + (weekOffset * 7));
    
    const dates = [];
    for (let i = 0; i < 5; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const weekDates = getWeekDates();
  const selectedDate = weekDates[selectedDayIndex];
  const classStudents = students.filter(s => s.class_id === selectedClass);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClass) fetchEvaluations();
  }, [selectedClass, weekOffset]);

  const fetchData = async () => {
    try {
      const [classesRes, studentsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/students')
      ]);
      
      // Admin et Direction voient toutes les classes
      // Les enseignants voient uniquement leurs classes
      const userRole = user?.role;
      let myClasses;
      
      if (userRole === 'admin' || userRole === 'direction') {
        myClasses = classesRes.data;
      } else {
        const teacherId = user?.teacher_id;
        myClasses = teacherId 
          ? classesRes.data.filter(c => c.teacher_id === teacherId)
          : [];
      }
      
      setClasses(myClasses);
      setStudents(studentsRes.data);
      if (myClasses.length > 0) setSelectedClass(myClasses[0].id);
    } catch (error) {
      toast.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluations = async () => {
    try {
      const res = await api.get(`/quranic-daily-evals?class_id=${selectedClass}`);
      const evalsMap = {};
      const progressMap = {};
      
      // Trier par date pour obtenir la dernière évaluation de chaque élève
      const sortedEvals = res.data.sort((a, b) => new Date(b.evaluation_date) - new Date(a.evaluation_date));
      
      sortedEvals.forEach(item => {
        const key = `${item.student_id}-${item.eval_type}-${item.evaluation_date}`;
        evalsMap[key] = item;
        
        // Stocker la dernière progression (sourate/verset) pour chaque élève (type bind uniquement)
        if (item.eval_type === 'bind' && item.surah_id && !progressMap[item.student_id]) {
          progressMap[item.student_id] = {
            lastSurah: item.surah_id,
            lastEndVerse: item.end_verse || 1,
            lastDate: item.evaluation_date
          };
        }
      });
      
      setEvaluations(evalsMap);
      setStudentProgress(progressMap);
      
      // Pré-remplir les champs avec la progression (continuer où l'élève s'est arrêté)
      const prefillData = {};
      Object.entries(progressMap).forEach(([studentId, prog]) => {
        const surah = SURAHS.find(s => s.id === prog.lastSurah);
        const nextVerse = (prog.lastEndVerse || 0) + 1;
        
        // Si on a fini la sourate, passer à la suivante
        if (surah && nextVerse > surah.verses) {
          const nextSurah = SURAHS.find(s => s.id === prog.lastSurah + 1);
          if (nextSurah) {
            prefillData[studentId] = {
              surah: String(nextSurah.id),
              startVerse: '1',
              endVerse: ''
            };
          }
        } else {
          prefillData[studentId] = {
            surah: String(prog.lastSurah),
            startVerse: String(nextVerse),
            endVerse: ''
          };
        }
      });
      setStudentSurahData(prefillData);
      
    } catch (error) {
      console.error(error);
    }
  };

  const updateEval = (studentId, status) => {
    const key = `${studentId}-${evalType}-${selectedDate}`;
    const surahData = studentSurahData[studentId] || {};
    setEvaluations(prev => ({
      ...prev,
      [key]: {
        student_id: studentId,
        class_id: selectedClass,
        evaluation_date: selectedDate,
        eval_type: evalType,
        status: status,
        surah_id: surahData.surah ? parseInt(surahData.surah) : null,
        start_verse: surahData.startVerse ? parseInt(surahData.startVerse) : null,
        end_verse: surahData.endVerse ? parseInt(surahData.endVerse) : null,
      }
    }));
  };

  const updateStudentSurah = (studentId, field, value) => {
    setStudentSurahData(prev => ({
      ...prev,
      [studentId]: { ...prev[studentId], [field]: value }
    }));
  };

  const getEval = (studentId) => {
    const key = `${studentId}-${evalType}-${selectedDate}`;
    return evaluations[key]?.status || '';
  };

  const handleSave = async () => {
    const evalsToSave = Object.values(evaluations).filter(e => e.status && e.evaluation_date === selectedDate);
    if (evalsToSave.length === 0) {
      toast.error('Aucune évaluation à enregistrer');
      return;
    }
    setSaving(true);
    try {
      await api.post('/quranic-daily-evals/batch', { evaluations: evalsToSave });
      toast.success(`${evalsToSave.length} évaluation(s) enregistrée(s)`);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  const getEvalCount = () => {
    return Object.values(evaluations).filter(e => e.status && e.evaluation_date === selectedDate).length;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="loader"></div></div>;
  }

  return (
    <div className="p-3 space-y-3" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Sélection classe */}
      <select 
        value={selectedClass} 
        onChange={(e) => setSelectedClass(e.target.value)}
        className="w-full text-lg font-bold"
      >
        {classes.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>

      {/* Navigation semaine */}
      <div className="flex items-center justify-between bg-white rounded-xl p-2 shadow-sm">
        <button 
          onClick={() => setWeekOffset(w => w - 1)} 
          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-xl"
        >
          ←
        </button>
        <div className="text-center">
          <div className="text-xs text-gray-500">Semaine</div>
          <div className="font-bold">{new Date(weekDates[0]).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {new Date(weekDates[4]).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</div>
        </div>
        <button 
          onClick={() => setWeekOffset(w => w + 1)} 
          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-xl"
        >
          →
        </button>
      </div>

      {/* Sélection jour - Tabs horizontaux */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {DAYS.map((day, idx) => (
          <button
            key={idx}
            onClick={() => setSelectedDayIndex(idx)}
            className={`flex-1 py-2 rounded-lg text-center transition-all ${
              selectedDayIndex === idx 
                ? 'bg-green-600 text-white shadow-md' 
                : 'text-gray-600'
            }`}
          >
            <div className="text-xs font-medium">{day.short}</div>
            <div className="text-lg font-bold">{weekDates[idx]?.slice(8)}</div>
          </button>
        ))}
      </div>

      {/* Type d'évaluation */}
      <div className="flex gap-2">
        {['bind', 'wett', 'nafarr'].map(type => (
          <button
            key={type}
            onClick={() => setEvalType(type)}
            className={`flex-1 py-3 rounded-xl font-bold text-lg transition-all ${
              evalType === type 
                ? 'bg-green-700 text-white shadow-lg' 
                : 'bg-white text-gray-700 border'
            }`}
          >
            {type === 'bind' ? 'بند' : type === 'wett' ? 'وط' : 'نفر'}
          </button>
        ))}
      </div>

      {/* Liste des élèves */}
      <div className="space-y-3">
        {classStudents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Aucun élève dans cette classe</div>
        ) : (
          classStudents.map((student, idx) => {
            const currentStatus = getEval(student.id);
            const progress = studentProgress[student.id];
            const lastSurah = progress ? SURAHS.find(s => s.id === progress.lastSurah) : null;
            
            return (
              <div key={student.id} className="bg-white rounded-xl p-4 shadow-sm">
                {/* En-tête élève */}
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center font-bold text-green-700">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-lg">{student.first_name} {student.last_name}</div>
                    <div className="text-xs text-gray-500">{student.matricule}</div>
                  </div>
                  {currentStatus && (
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xl"
                      style={{ background: STATUSES.find(s => s.value === currentStatus)?.color }}
                    >
                      {STATUSES.find(s => s.value === currentStatus)?.label}
                    </div>
                  )}
                </div>

                {/* Affichage de la progression actuelle */}
                {evalType === 'bind' && progress && lastSurah && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 font-bold">📖 Progression:</span>
                      <span className="text-blue-800">
                        {lastSurah.name} ({lastSurah.name_fr}) - Verset {progress.lastEndVerse}
                      </span>
                    </div>
                    <div className="text-xs text-blue-500 mt-1">
                      Dernière évaluation: {new Date(progress.lastDate).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                )}

                {/* Champs Sourate/Versets pour Bind */}
                {evalType === 'bind' && (
                  <div className="space-y-2 mb-3 bg-green-50 p-3 rounded-lg">
                    <div>
                      <label className="text-xs text-gray-600 block mb-1 font-medium">سورة (Sourate)</label>
                      <select
                        value={studentSurahData[student.id]?.surah || ''}
                        onChange={(e) => updateStudentSurah(student.id, 'surah', e.target.value)}
                        className="w-full p-3 border-2 border-green-200 rounded-lg text-base"
                      >
                        <option value="">-- Choisir une sourate --</option>
                        {SURAHS.map(s => (
                          <option key={s.id} value={s.id}>
                            {s.id}. {s.name} ({s.name_fr}) - {s.verses} آية
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">من آية (Du verset)</label>
                        <input
                          type="number"
                          min="1"
                          max={SURAHS.find(s => s.id === parseInt(studentSurahData[student.id]?.surah))?.verses || 286}
                          placeholder="1"
                          value={studentSurahData[student.id]?.startVerse || ''}
                          onChange={(e) => updateStudentSurah(student.id, 'startVerse', e.target.value)}
                          className="w-full p-3 border-2 border-green-200 rounded-lg text-center text-lg"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">إلى آية (Au verset)</label>
                        <input
                          type="number"
                          min="1"
                          max={SURAHS.find(s => s.id === parseInt(studentSurahData[student.id]?.surah))?.verses || 286}
                          placeholder="10"
                          value={studentSurahData[student.id]?.endVerse || ''}
                          onChange={(e) => updateStudentSurah(student.id, 'endVerse', e.target.value)}
                          className="w-full p-3 border-2 border-green-200 rounded-lg text-center text-lg"
                        />
                      </div>
                    </div>
                    {studentSurahData[student.id]?.surah && (
                      <div className="text-xs text-green-700 text-center">
                        {SURAHS.find(s => s.id === parseInt(studentSurahData[student.id]?.surah))?.name} - 
                        Max: {SURAHS.find(s => s.id === parseInt(studentSurahData[student.id]?.surah))?.verses} versets
                      </div>
                    )}
                  </div>
                )}

                {/* Boutons de statut */}
                <div className="grid grid-cols-4 gap-2">
                  {STATUSES.map(s => (
                    <button
                      key={s.value}
                      onClick={() => updateEval(student.id, s.value)}
                      className={`py-3 rounded-xl font-bold text-white transition-all ${
                        currentStatus === s.value 
                          ? 'ring-4 ring-offset-2 ring-gray-400 scale-105' 
                          : 'opacity-70'
                      }`}
                      style={{ background: s.color }}
                    >
                      <div className="text-2xl">{s.label}</div>
                      <div className="text-xs mt-1">{s.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Bouton Enregistrer - NON flottant */}
      {classStudents.length > 0 && (
        <button 
          onClick={handleSave} 
          disabled={saving || getEvalCount() === 0}
          className={`w-full py-4 rounded-xl font-bold text-lg text-white transition-all mb-4 ${
            saving || getEvalCount() === 0 
              ? 'bg-gray-300' 
              : 'bg-green-600 shadow-lg'
          }`}
        >
          {saving ? 'Enregistrement...' : `Enregistrer ${getEvalCount()} évaluation(s) - ${DAYS[selectedDayIndex].name}`}
        </button>
      )}
    </div>
  );
}

// Monthly Evaluation Page
function MonthlyEvalPage() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [evaluations, setEvaluations] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { t, isRTL } = useLang();

  const REASONS = [
    { value: 'suivi', label: 'حـ' },
    { value: 'non_suivi', label: 'لم' },
    { value: 'absent', label: 'غ' },
    { value: 'malade', label: 'م' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClass) fetchEvaluations();
  }, [selectedClass, selectedMonth]);

  const fetchData = async () => {
    try {
      const [classesRes, studentsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/students')
      ]);
      
      // Admin et Direction voient toutes les classes
      // Les enseignants voient uniquement leurs classes
      const userRole = user?.role;
      let myClasses;
      
      if (userRole === 'admin' || userRole === 'direction') {
        myClasses = classesRes.data;
      } else {
        const teacherId = user?.teacher_id;
        myClasses = teacherId 
          ? classesRes.data.filter(c => c.teacher_id === teacherId)
          : [];
      }
      
      setClasses(myClasses);
      setStudents(studentsRes.data);
      if (myClasses.length > 0) setSelectedClass(myClasses[0].id);
    } catch (error) {
      toast.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluations = async () => {
    try {
      const res = await api.get(`/monthly-evals?class_id=${selectedClass}&month=${selectedMonth}&year=${new Date().getFullYear()}`);
      const evalsMap = {};
      res.data.forEach(item => {
        evalsMap[item.student_id] = item;
      });
      setEvaluations(evalsMap);
    } catch (error) {
      console.error(error);
    }
  };

  const classStudents = students.filter(s => s.class_id === selectedClass);

  const updateEval = (studentId, field, value) => {
    setEvaluations(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        student_id: studentId,
        class_id: selectedClass,
        evaluator_id: user?.id,
        month: selectedMonth,
        year: new Date().getFullYear(),
        [field]: value,
      }
    }));
  };

  const getEval = (studentId) => evaluations[studentId] || {};

  const handleSave = async () => {
    const evalsToSave = Object.values(evaluations).filter(e => e.student_id);
    if (evalsToSave.length === 0) {
      toast.error(t('noEval'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/monthly-evals/batch', { evaluations: evalsToSave });
      toast.success(t('saved'));
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="loader"></div></div>;
  }

  return (
    <div className="p-4 space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="grid grid-cols-2 gap-2">
        <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
          {t('months').map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {classStudents.map(student => {
          const evalData = getEval(student.id);
          return (
            <div key={student.id} className="card p-4">
              <h4 className="font-bold mb-3">{student.first_name} {student.last_name}</h4>
              
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="text-center">
                  <label className="text-xs text-gray-500 block mb-1">بند</label>
                  <input
                    type="number"
                    min="0"
                    value={evalData.count_bind || ''}
                    onChange={(e) => updateEval(student.id, 'count_bind', parseInt(e.target.value) || 0)}
                    className="text-center"
                  />
                </div>
                <div className="text-center">
                  <label className="text-xs text-gray-500 block mb-1">وط</label>
                  <input
                    type="number"
                    min="0"
                    value={evalData.count_wett || ''}
                    onChange={(e) => updateEval(student.id, 'count_wett', parseInt(e.target.value) || 0)}
                    className="text-center"
                  />
                </div>
                <div className="text-center">
                  <label className="text-xs text-gray-500 block mb-1">نفر</label>
                  <input
                    type="number"
                    min="0"
                    value={evalData.count_nafarr || ''}
                    onChange={(e) => updateEval(student.id, 'count_nafarr', parseInt(e.target.value) || 0)}
                    className="text-center"
                  />
                </div>
              </div>

              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-500">{t('reasons')}:</span>
                {REASONS.map(r => (
                  <button
                    key={r.value}
                    onClick={() => updateEval(student.id, 'absence_reason', r.value)}
                    className={`status-btn ${evalData.absence_reason === r.value ? 'ring-2 ring-green-500' : 'opacity-50'}`}
                    style={{ background: r.value === 'suivi' ? '#22c55e' : r.value === 'non_suivi' ? '#ef4444' : r.value === 'absent' ? '#6b7280' : '#eab308' }}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <button onClick={handleSave} disabled={saving} className="btn-primary">
        {saving ? t('saving') : t('save')}
      </button>
    </div>
  );
}

// Trimestrial Evaluation Page
function TrimestrialEvalPage() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [trimester, setTrimester] = useState(1);
  const [evaluations, setEvaluations] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { t, isRTL } = useLang();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, studentsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/students')
      ]);
      
      // Admin et Direction voient toutes les classes
      // Les enseignants voient uniquement leurs classes
      const userRole = user?.role;
      let myClasses;
      
      if (userRole === 'admin' || userRole === 'direction') {
        myClasses = classesRes.data;
      } else {
        const teacherId = user?.teacher_id;
        myClasses = teacherId 
          ? classesRes.data.filter(c => c.teacher_id === teacherId)
          : [];
      }
      
      setClasses(myClasses);
      setStudents(studentsRes.data);
      if (myClasses.length > 0) setSelectedClass(myClasses[0].id);
    } catch (error) {
      toast.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };

  const classStudents = students.filter(s => s.class_id === selectedClass);

  const updateEval = (studentId, field, value) => {
    setEvaluations(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        student_id: studentId,
        class_id: selectedClass,
        evaluator_id: user?.id,
        trimester: trimester,
        year: new Date().getFullYear(),
        [field]: value,
      }
    }));
  };

  const getEval = (studentId) => evaluations[studentId] || {};

  const handleSave = async () => {
    const evalsToSave = Object.values(evaluations).filter(e => e.student_id);
    if (evalsToSave.length === 0) {
      toast.error(t('noEval'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/trimestrial-evals/batch', { evaluations: evalsToSave });
      toast.success(t('saved'));
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="loader"></div></div>;
  }

  const trimesterLabels = isRTL ? ['الفصل ١', 'الفصل ٢', 'الفصل ٣'] : ['Trimestre 1', 'Trimestre 2', 'Trimestre 3'];

  return (
    <div className="p-4 space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="grid grid-cols-2 gap-2">
        <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={trimester} onChange={(e) => setTrimester(parseInt(e.target.value))}>
          {trimesterLabels.map((label, i) => <option key={i} value={i + 1}>{label}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {classStudents.map(student => {
          const evalData = getEval(student.id);
          return (
            <div key={student.id} className="card p-4">
              <h4 className="font-bold mb-3">{student.first_name} {student.last_name}</h4>
              
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{t('level')}</label>
                  <input
                    value={evalData.level || ''}
                    onChange={(e) => updateEval(student.id, 'level', e.target.value)}
                    placeholder="Ex: سورة البقرة"
                    dir="rtl"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">{t('grade')} /20</label>
                    <input
                      type="number"
                      min="0"
                      max="20"
                      value={evalData.grade || ''}
                      onChange={(e) => updateEval(student.id, 'grade', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">{t('appreciation')}</label>
                    <select
                      value={evalData.appreciation || ''}
                      onChange={(e) => updateEval(student.id, 'appreciation', e.target.value)}
                    >
                      <option value="">--</option>
                      <option value="excellent">{t('excellent')}</option>
                      <option value="good">{t('good')}</option>
                      <option value="average">{t('average')}</option>
                      <option value="poor">{t('poor')}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button onClick={handleSave} disabled={saving} className="btn-primary">
        {saving ? t('saving') : t('save')}
      </button>
    </div>
  );
}

// Bottom Navigation
function BottomNav() {
  const location = useLocation();
  const { logout } = useAuth();
  const { t, isRTL } = useLang();
  
  const navItems = [
    { path: '/', icon: Home, label: t('home') },
    { path: '/weekly', icon: Calendar, label: t('week') },
    { path: '/monthly', icon: BookOpen, label: t('month') },
    { path: '/trimester', icon: ClipboardList, label: t('trimester') },
  ];

  return (
    <nav className="bottom-nav" dir={isRTL ? 'rtl' : 'ltr'}>
      {navItems.map(item => (
        <Link
          key={item.path}
          to={item.path}
          className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
        >
          <item.icon />
          {item.label}
        </Link>
      ))}
      <button onClick={logout} className="nav-item">
        <LogOut />
        {t('logout')}
      </button>
    </nav>
  );
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const { t, isRTL } = useLang();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loader"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  
  return (
    <div className="app-container" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="header">
        <h1 className="text-xl font-bold">دار الإيمان</h1>
        <p className="text-sm text-green-200">{t('teacherSpace')}</p>
      </header>
      <main className="page-content">
        {children}
      </main>
      <BottomNav />
    </div>
  );
}

// Main App
function AppContent() {
  const { showLangPicker } = useLang();

  if (showLangPicker) {
    return <LanguagePicker />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/weekly" element={<ProtectedRoute><WeeklyEvalPage /></ProtectedRoute>} />
      <Route path="/monthly" element={<ProtectedRoute><MonthlyEvalPage /></ProtectedRoute>} />
      <Route path="/trimester" element={<ProtectedRoute><TrimestrialEvalPage /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <AuthProvider>
          <Toaster position="top-center" />
          <AppContent />
        </AuthProvider>
      </LangProvider>
    </BrowserRouter>
  );
}
